import { Result } from "../models/interfaces";
import { BaseService } from "./BaseService";

export type LoanDto = {
  id?: number;
  repaymentAccountId?: number;
  repaymentAccountName?: string;
  loanAccountId?: number;
  loanAccountName?: string;
  loanType?: string;
  reason?: string;
  amount?: number;
  startDate?: Date;
  endDate?: Date;
  duration?: number; //in days
  rate?: number;
  repaymentAmount?: number;
  balance?: number;
  alternateRepaymentAccountIds?: number[];
};

export type RepaymentScheduleDto = {
  id: number;
  loanId: number;
  repaymentDate: Date;
  repaymentStatus: number;
  interestPaymentStatus: number;
  amount: number;
};

export class LoanService extends BaseService {
  async getRepaymentSchedule(
    loanId: any
  ): Promise<Result<RepaymentScheduleDto[]>> {
    var res = await this.get<Result<RepaymentScheduleDto[]>>(
      `${loanId}/schedules`
    );
    return res;
  }
  private loanApi: string = "loans";

  async getLoans(params: {
    pageIndex: number;
    recordCount: number;
    loanAccountName?: string;
  }): Promise<Result<LoanDto[]>> {
    const queryParams = this.ToQueryString(params);
    return this.get(`${this.loanApi}${queryParams}`);
  }

  async getLoan(id: number): Promise<Result<LoanDto>> {
    return this.get(`${this.loanApi}/${id}`);
  }

  async deleteLoan(id: number): Promise<Result<boolean>> {
    return this.delete(`${this.loanApi}/${id}`);
  }

  async disabledOrEnableLoan(
    id: number,
    value: boolean
  ): Promise<Result<boolean>> {
    const action = value ? "enable" : "disable";
    return this.patch(`${this.loanApi}/${id}/${action}`);
  }

  async postManualRepayment(params: {
    loanId: number;
    amount: number;
  }): Promise<Result<boolean>> {
    return this.post(`${this.loanApi}/${params.loanId}/repayment`);
  }

  async postRepayment(
    scheduleId: number,
    value: number
  ): Promise<Result<boolean>> {
    return this.post(`${this.loanApi}/repayments/${scheduleId}`, value);
  }

  async postInterest(
    scheduleId: number,
    value: number
  ): Promise<Result<boolean>> {
    return this.post(`${this.loanApi}/interest/${scheduleId}`, value);
  }

  async reverseRepayment(
    scheduleId: number,
    reason?: string
  ): Promise<Result<boolean>> {
    return this.post(
      `${this.loanApi}/repayments/${scheduleId}/reverse`,
      reason
    );
  }

  async reverseInterestPayment(
    scheduleId: number,
    reason: string
  ): Promise<Result<boolean>> {
    return this.post(`${this.loanApi}/interest/${scheduleId}/reverse`, reason);
  }

  async deleteInterestPayment(
    scheduleId: number,
    reason: string
  ): Promise<Result<boolean>> {
    return this.delete(`${this.loanApi}/interest/${scheduleId}`, reason);
  }

  async deleteRepayment(
    scheduleId: number,
    reason: string
  ): Promise<Result<boolean>> {
    return this.delete(`${this.loanApi}/repayments/${scheduleId}`, reason);
  }

  async generateSchedule(loan: LoanDto): Promise<Result<boolean>> {
    return this.post(`${this.loanApi}/schedules/generate`, loan);
  }

  async deleteScheduleEntry(scheduleId: number): Promise<Result<boolean>> {
    return this.delete(`${this.loanApi}/schedules/${scheduleId}`);
  }

  async deleteSchedule(loanId: number): Promise<Result<boolean>> {
    return this.delete(`${this.loanApi}/${loanId}/schedules`);
  }

  async postLoan(id: number): Promise<any> {
    return this.post(`${this.loanApi}/${id}/post`);
  }

  async reverseLoanPosting(id: number): Promise<any> {
    return this.post(`${this.loanApi}/${id}/post`);
  }
}

export default new LoanService();
